/**
 * ShopifyBillingPanel — VP-329. Panel planu dla merchanta, który zainstalował
 * aplikację ze Shopify App Store.
 *
 * Zastępuje mu siatkę planów Stripe (`billing-panel.tsx`): Shopify wymaga (§1.2.1),
 * żeby plan wybierał i zmieniał na hostowanej przez siebie stronie planów, więc
 * jedyne, co tu jest, to stan planu i przycisk prowadzący do panelu sklepu.
 * Zmiana i anulowanie planu dzieją się w całości po stronie Shopify (§1.2.3).
 */
import { useTranslation } from "react-i18next";
import { ExternalLink, Loader2, ShoppingBag } from "lucide-react";
import { useShopifyBillingStatus } from "@/hooks/useBillingProvider";

/** Status z Partner API → klucz tłumaczenia + kolorystyka odznaki (jak w fakturach). */
const STATUS_LABELS: Record<string, { key: string; className: string }> = {
  active: {
    key: "shopify.statusActive",
    className: "bg-green-600/15 text-green-400 border-green-500/25",
  },
  trial: {
    key: "shopify.statusTrial",
    className: "bg-blue-600/15 text-blue-400 border-blue-500/25",
  },
  cancelling: {
    key: "shopify.statusCancelling",
    className: "bg-orange-600/15 text-orange-400 border-orange-500/25",
  },
  none: {
    key: "shopify.statusNone",
    className: "bg-white/[0.06] text-white/50 border-white/[0.12]",
  },
  unknown_plan: {
    key: "shopify.statusUnknown",
    className: "bg-white/[0.06] text-white/50 border-white/[0.12]",
  },
};

export default function ShopifyBillingPanel() {
  const { t } = useTranslation("billingPanel");
  const { data, isLoading } = useShopifyBillingStatus();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-white/40">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const status = STATUS_LABELS[data?.subscriptionStatus ?? "none"] ?? STATUS_LABELS.none;
  const hasPlan = Boolean(data?.planKey);
  const manageUrl = data?.manageUrl ?? null;

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6 space-y-5">
      <div className="flex items-start gap-3">
        <ShoppingBag className="w-5 h-5 text-white/40 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="text-white text-sm font-medium">{t("shopify.title")}</h3>
          <p className="text-white/40 text-xs mt-1">{t("shopify.subtitle")}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div>
          <p className="text-white/40 text-xs">{t("shopify.currentPlan")}</p>
          <p className="text-white text-sm mt-0.5">{data?.planKey ?? t("shopify.noPlan")}</p>
        </div>
        <span className={`px-2 py-0.5 rounded-md border text-xs font-medium ${status.className}`}>
          {t(status.key)}
        </span>
      </div>

      {manageUrl ? (
        <a
          href={manageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.08] hover:bg-white/[0.12] text-white text-sm font-medium transition-colors"
        >
          {hasPlan ? t("shopify.managePlan") : t("shopify.choosePlan")}
          <ExternalLink className="w-4 h-4" />
        </a>
      ) : (
        <p className="text-white/40 text-xs">{t("shopify.linkUnavailable")}</p>
      )}

      <p className="text-white/30 text-xs">{t("shopify.invoicesNote")}</p>
    </div>
  );
}
